import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { Upload, X, ArrowRight, ArrowLeft, Loader2, PoundSterling } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateAsset = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  
  const [beforeImage, setBeforeImage] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  
  const [jobDescription, setJobDescription] = useState('');
  const [jobValue, setJobValue] = useState('');  // Optional job value
  const [loading, setLoading] = useState(false);

  const needsSubscription = !user?.is_subscribed && user?.free_asset_used;

  const handleImageSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large (max 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'before') {
        setBeforeImage(file);
        setBeforePreview(e.target.result);
      } else {
        setAfterImage(file);
        setAfterPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!beforeImage || !afterImage) {
      toast.error('Please upload both Before and After photos');
      setStep(1);
      return;
    }

    if (!jobDescription.trim()) {
      toast.error('Please describe the job');
      setStep(2);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('job_description', jobDescription);
      formData.append('images[0]', beforeImage);
      formData.append('images[1]', afterImage);
      
      // Add job value if provided (optional)
      if (jobValue.trim()) {
        formData.append('job_value', jobValue.trim());
      }

      const response = await axios.post(
        `${API_URL}/api/assets/create-with-images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Sales asset created!');
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Subscribe to create more assets.');
        navigate('/subscribe');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create asset');
      }
    } finally {
      setLoading(false);
    }
  };

  if (needsSubscription) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 px-4 pb-12">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <h1 className="text-2xl font-bold text-[#0a2540] mb-4">
                You've used your free asset
              </h1>
              <p className="text-slate-600 mb-6">
                Subscribe for £9/month to create unlimited sales assets.
              </p>
              <Button
                onClick={() => navigate('/subscribe')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
              >
                Subscribe now
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    s === step 
                      ? 'bg-[#0a2540] text-white' 
                      : s < step 
                        ? 'bg-[#d4af37] text-[#0a2540]'
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-0.5 ${s < step ? 'bg-[#d4af37]' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Upload Photos */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0a2540] mb-2">
                  Upload your photos
                </h1>
                <p className="text-slate-600">
                  Add your before and after photos
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                <div className="grid grid-cols-2 gap-6">
                  {/* Before Photo */}
                  <div>
                    <Label className="text-slate-700 font-medium mb-3 block text-center">
                      Before Photo
                    </Label>
                    {beforePreview ? (
                      <div className="relative aspect-square">
                        <img
                          src={beforePreview}
                          alt="Before"
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <span className="absolute bottom-2 left-2 text-xs bg-black/70 text-white px-2 py-1 rounded font-medium">Before</span>
                        <button
                          type="button"
                          onClick={() => { setBeforeImage(null); setBeforePreview(null); }}
                          className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => beforeInputRef.current?.click()}
                        className="aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#0a2540]/50 hover:bg-slate-50 transition-all"
                      >
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-500">Upload</span>
                      </div>
                    )}
                    <input
                      ref={beforeInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'before')}
                      className="hidden"
                    />
                  </div>

                  {/* After Photo */}
                  <div>
                    <Label className="text-slate-700 font-medium mb-3 block text-center">
                      After Photo
                    </Label>
                    {afterPreview ? (
                      <div className="relative aspect-square">
                        <img
                          src={afterPreview}
                          alt="After"
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <span className="absolute bottom-2 left-2 text-xs bg-[#d4af37] text-[#0a2540] px-2 py-1 rounded font-bold">After</span>
                        <button
                          type="button"
                          onClick={() => { setAfterImage(null); setAfterPreview(null); }}
                          className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => afterInputRef.current?.click()}
                        className="aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#d4af37]/50 hover:bg-amber-50/30 transition-all"
                      >
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-500">Upload</span>
                      </div>
                    )}
                    <input
                      ref={afterInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageSelect(e, 'after')}
                      className="hidden"
                    />
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!beforeImage || !afterImage}
                  className="w-full mt-8 h-12 bg-[#0a2540] hover:bg-[#0a2540]/90 text-white font-semibold"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Describe Job */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0a2540] mb-2">
                  Describe the job
                </h1>
                <p className="text-slate-600">
                  This helps generate better social media copy
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <Label className="text-slate-700 font-medium mb-2 block">
                      Job Description
                    </Label>
                    <Textarea
                      placeholder="E.g. 'Complete bathroom renovation in Melbourne. Customer wanted a modern spa-like feel with new tiles, vanity, and fixtures.'"
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      className="min-h-[120px] bg-[#f8f9fa] border-slate-200 focus:border-[#0a2540] resize-none"
                    />
                  </div>

                  {/* Job Value - Optional */}
                  <div>
                    <Label className="text-slate-700 font-medium mb-2 block">
                      Approximate value of the work (£)
                      <span className="font-normal text-slate-400 ml-2">Optional</span>
                    </Label>
                    <div className="relative">
                      <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 2,500"
                        value={jobValue}
                        onChange={(e) => setJobValue(e.target.value)}
                        className="pl-9 bg-[#f8f9fa] border-slate-200 focus:border-[#0a2540]"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">
                      This won't be shown publicly — it just helps tailor the wording.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-3">Your photos:</p>
                  <div className="flex gap-4">
                    <div className="relative w-20 h-20">
                      <img src={beforePreview} alt="Before" className="w-full h-full object-cover rounded-lg" />
                      <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-black/70 text-white text-center rounded-b-lg py-0.5">Before</span>
                    </div>
                    <div className="relative w-20 h-20">
                      <img src={afterPreview} alt="After" className="w-full h-full object-cover rounded-lg" />
                      <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-[#d4af37] text-[#0a2540] text-center font-bold rounded-b-lg py-0.5">After</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-6">
                  <Button onClick={() => setStep(1)} variant="outline" className="flex-1 h-12">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!jobDescription.trim()}
                    className="flex-1 h-12 bg-[#0a2540] hover:bg-[#0a2540]/90 text-white font-semibold"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Generate */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-[#0a2540] mb-2">
                  Ready to generate
                </h1>
                <p className="text-slate-600">
                  Review and create your sales asset
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-3">Photos</p>
                    <div className="flex gap-4">
                      <div className="relative w-24 h-24">
                        <img src={beforePreview} alt="Before" className="w-full h-full object-cover rounded-xl" />
                        <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">Before</span>
                      </div>
                      <div className="relative w-24 h-24">
                        <img src={afterPreview} alt="After" className="w-full h-full object-cover rounded-xl" />
                        <span className="absolute bottom-1 left-1 text-[10px] bg-[#d4af37] text-[#0a2540] px-1.5 py-0.5 rounded font-bold">After</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-slate-500 mb-2">Job description</p>
                    <p className="text-[#0a2540] bg-slate-50 p-4 rounded-lg text-sm">
                      {jobDescription}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <Button onClick={() => setStep(2)} variant="outline" className="flex-1 h-12" disabled={loading}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 h-12 bg-[#d4af37] hover:bg-[#c5a028] text-[#0a2540] font-bold"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Sales Asset'
                    )}
                  </Button>
                </div>

                {!user?.free_asset_used && (
                  <p className="text-center text-sm text-slate-500 mt-4">
                    This is your free asset • No credit card required
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAsset;
